// NATIVE
import React, { useEffect, useState } from 'react';

// INSTALLED
import { Breadcrumbs, Button, Divider, Typography } from '@mui/material';

// CUSTOM
import { closeSessionGlobal, hasPemissions } from '../../commons/utils';
import { SnackAlert } from '../../components/SnackAlert';
import { mtDividerUpTable, timeToClosedToast } from '../../commons';
import { TableTipoNovedad } from '../../components/tables/TableTipoNovedad';
import { BreadCrumbsAndTitle } from '../../components/generics/BreadCrumbsAndTitle';
import { CreateDialogTipoNovedad } from '../../components/modals/tipoNovedades/CreateDialogTipoNovedad';
import { AlertsInformativos } from '../../components/generics/AlertsInformativos';
import { alertInformativo } from '../../commons/texts';
import { useDispatch } from 'react-redux';
import { ModalSessionExpired } from '../../components/generics/ModalSessionExpired';
import MultipleSnackbars from '../../components/MultipleSnackbars';
import { getDatasCreateTipoNovedadStore } from '../../store/tipo_novedad';

/**
 * `TipoNovedades` is a React functional component that renders a page for managing "Tipo Novedades".
 * It includes a button to create a new "Tipo Novedad" if the user has the necessary permissions.
 * The component also displays a breadcrumb and title, a divider, a table of "Tipo Novedades",
 * and a dialog for creating a new "Tipo Novedad".
 *
 * @component
 *
 * @example
 * return (
 *   <TipoNovedades />
 * )
 *
 * @returns {JSX.Element} The rendered component.
 *
 * @remarks
 * This component uses several hooks:
 * - `useState` to manage the state of the modal, creation permissions, and snack alerts.
 * - `useEffect` to check if the user has permissions to create a new "Tipo Novedad" on component mount.
 *
 * The component also defines several functions:
 * - `canCheckCanCreate`: Checks if the user has permissions to create a new "Tipo Novedad".
 * - `openModal`: Opens the modal for creating a new "Tipo Novedad" if the user has the necessary permissions.
 * - `handleCloseDialogNovedad`: Closes the modal for creating a new "Tipo Novedad".
 * - `validateLsPermissions`: Displays an error message if the user does not have the necessary permissions.
 *
 * @requires hasPemissions
 * @requires validateLsPermissions
 * @requires SnackAlert
 * @requires BreadCrumbsAndTitle
 * @requires Button
 * @requires Divider
 * @requires CreateDialogTipoNovedad
 * @requires TableTipoNovedad
 */
export const TipoNovedades = () => {
  // DISPATCH
  const dispatch = useDispatch();

  // USE STATE
  const [openNovedad, setOpenNovedad] = useState(false);

  const [canCreate, setCanCreate] = useState(false);
  const [modalSessionExpired, setModalSessionExpired] = React.useState(false);
  const [messagesAlerts, setMessagesAlerts] = useState<any>([]);
  const [timeAlert, setTimeAlert] = useState(timeToClosedToast);

  const [alert_snack, setAlertSnack] = React.useState(false);
  const [msgSnack, setMsgSnack] = React.useState({
    msg: '',
    status: 'success',
  });

  // USE EFFECT
  useEffect(() => {
    canCheckCanCreate();
  }, []);

  // CONST FUNCTIONS
  const canCheckCanCreate = async () => {
    const havePermissions = await hasPemissions(
      'modulo.thumano',
      'modulo.novedades',
      'create',
      'modulo.tiponovedades'
    );

    setCanCreate(havePermissions);
  };

  const openModal = async () => {
    // modulo.tiponovedades                             nombre modulo     sub modulo    permission
    const havePermissions = await hasPemissions(
      'modulo.thumano',
      'modulo.novedades',
      'create',
      'modulo.tiponovedades'
    );

    if (!havePermissions) {
      await validateLsPermissions('No tienes pemisos para (Crear) tipo novedad.');

      return false;
    }

    const response = await dispatch(getDatasCreateTipoNovedadStore());

    const result = await validateResponseGetDataForCreate(response, ' (Crear Tipo Novedad.) ');

    if (!result) {
      return false;
    }

    setOpenNovedad(true);
  };

  const handleCloseDialogNovedad = () => {
    setOpenNovedad(false);
  };

  const validateResponseGetDataForCreate = async (response: any, textActionConcat: string) => {
    if (response.code == 401) {
      await localStorage.removeItem('lastSelectedQypht');
      await closeSessionGlobal();
      setModalSessionExpired(true);
      return false;
    }

    // 403 NO TIENE PERMISSO PARA REALIZAR ESA ACCION
    if (!response.ok && response.code == 403) {
      const errors: any = {
        msg: response.errors[0] + ' ' + textActionConcat,
        status: 'error',
      };

      await setMsgSnack(errors);
      await setAlertSnack(true);

      return false;
    }

    return true;
  };

  const validateLsPermissions = async (message: string) => {
    const errors: any = {
      msg: message,
      status: 'error',
    };

    await setMsgSnack(errors);
    await setAlertSnack(true);
  };

  return (
    <>
      <ModalSessionExpired open={modalSessionExpired} />
      <MultipleSnackbars
        messagesList={messagesAlerts}
        position={'bottom-left'}
        autoClose={timeAlert}
      />
      <SnackAlert
        open={alert_snack}
        onclick={setAlertSnack}
        text={msgSnack.msg}
        severity={msgSnack.status}
      />

      <BreadCrumbsAndTitle
        title="Listado Tipo Novedades"
        mainModule="Talento Humano"
        parent="Listado Tipo Novedades"
        action=""
      />

      <div style={{ display: 'flex', justifyContent: 'end' }}>
        {canCreate && (
          <Button variant="contained" sx={{ marginBottom: 3 }} onClick={openModal}>
            Crear Tipo Novedad
          </Button>
        )}
      </div>

      <AlertsInformativos type={'info'} title={'info'} content={alertInformativo.tipoNovedades} />
      <Divider sx={{ marginBottom: mtDividerUpTable }} />

      <CreateDialogTipoNovedad
        open={openNovedad}
        handleCloseDialogNovedad={handleCloseDialogNovedad}
      />
      <TableTipoNovedad />
    </>
  );
};
